/**
 * Utilidades de fecha para timezone America/Los_Angeles
 * REGLA: Todos los bloqueos de fecha usan YYYY-MM-DD en horario de California
 */

const LA_TIMEZONE = 'America/Los_Angeles';

/**
 * Convierte cualquier Date a string YYYY-MM-DD en timezone de California
 * @param input - Date object
 * @returns string en formato YYYY-MM-DD
 */
export function toLA_yyyy_mm_dd(input: Date): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: LA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(input);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;

  return `${year}-${month}-${day}`;
}

/**
 * Obtiene la fecha actual en California como YYYY-MM-DD
 */
export function getTodayLA(): string {
  return toLA_yyyy_mm_dd(new Date());
}

/**
 * Convierte string YYYY-MM-DD a Date object en California
 */
export function fromLA_yyyy_mm_dd(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Compara dos fechas YYYY-MM-DD
 */
export function compareDates(date1: string, date2: string): number {
  if (date1 < date2) return -1;
  if (date1 > date2) return 1;
  return 0;
}

/**
 * Verifica si una fecha es pasada (en California)
 */
export function isPastDate(dateStr: string): boolean {
  const today = getTodayLA();
  return compareDates(dateStr, today) < 0;
}

/**
 * Obtiene el primer día del mes en California
 */
export function getFirstDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1);
}

/**
 * Obtiene el último día del mes
 */
export function getLastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

/**
 * Obtiene el día de la semana (0 = domingo, 6 = sábado)
 */
export function getDayOfWeek(date: Date): number {
  return date.getDay();
}

/**
 * Formatea fecha para mostrar (ej: "15 de enero de 2025")
 */
export function formatDateDisplay(dateStr: string, locale: string = 'es-MX'): string {
  // Validar que dateStr sea válido
  if (!dateStr || typeof dateStr !== 'string') {
    return 'Fecha no disponible';
  }

  // Validar formato YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) {
    return 'Fecha inválida';
  }

  try {
    const date = fromLA_yyyy_mm_dd(dateStr);
    
    // Verificar que la fecha sea válida
    if (isNaN(date.getTime())) {
      return 'Fecha inválida';
    }

    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: LA_TIMEZONE,
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', dateStr, error);
    return 'Fecha inválida';
  }
}
